import { z } from 'zod/v4';

export const VectorSearchQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(1).max(500).default(15),
  searchQuery: z.string().min(1).max(255),
  excludeCompanyIds: z.string().min(1).nullable().optional(),
  /** ISO country code. Boosts matching candidates in ranking — does not filter results. */
  country: z.string().min(1).max(255).nullable().optional(),
  /** Facility-process id. Boosts matching candidates in ranking — does not filter results. */
  facilityProcess: z.string().min(1).max(255).nullable().optional(),
});

export type VectorSearchQuery = z.infer<typeof VectorSearchQuerySchema>;

/** How `searchQuery` was split into a company name plus structured facets. */
export const ParsedSearchFacetsSchema = z.object({
  /** The query with recognised country / facility-process words removed. */
  residual: z.string(),
  /** ISO alpha-2 code parsed out of the query, if any. */
  country: z.string().nullable(),
  /** Facility-process id parsed out of the query, if any. */
  facilityProcess: z.string().nullable(),
});

export type ParsedSearchFacetsView = z.infer<typeof ParsedSearchFacetsSchema>;

export const PaginatedMetaSchema = z.object({
  count: z.number(),
  page: z.number(),
  totalCount: z.number(),
  pageSize: z.number(),
  parsed: ParsedSearchFacetsSchema,
});

export type PaginatedMeta = z.infer<typeof PaginatedMetaSchema>;

export const VectorSearchResultSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    byCompanyId: z.string(),
    companyId: z.string(),
    vectorDistance: z.number(),
    vectorSimilarity: z.number(),
    stringSimilarity: z.number(),
    matchScore: z.number(),
    countryMatch: z.boolean(),
    facilityProcessMatch: z.boolean(),
    officialName: z.string().nullable(),
    name: z.string().nullable(),
    displayName: z.string().nullable(),
    country: z.string().nullable(),
    countryName: z.string().nullable(),
    city: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    zipCode: z.string().nullable(),
    facilityProcesses: z.string().nullable(),
    connectionTypeId: z.string().nullable(),
    imageList: z.array(z.unknown()),
    featuredImageFileUrl: z.string().nullable(),
    originalFeaturedImageFileUrl: z.string().nullable(),
    originalVideoFileUrl: z.string().nullable(),
    supplyChainTier: z.array(z.unknown()),
    relationshipType: z.array(z.unknown()),
    responsiblePerson: z.array(z.unknown()),
  })
  .passthrough();

export type VectorSearchResult = z.infer<typeof VectorSearchResultSchema>;

export const VectorSearchResponseSchema = z.object({
  data: z.array(VectorSearchResultSchema),
  meta: PaginatedMetaSchema,
});

export type VectorSearchResponse = z.infer<typeof VectorSearchResponseSchema>;
